import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const formatRange = (rule) => {
  if (rule.min_price == null && rule.max_price == null) {
    return <Chip label="Any base price" size="small" sx={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 500 }} />;
  }
  const min = rule.min_price != null ? `€${parseFloat(rule.min_price).toFixed(2)}` : '€0';
  const max = rule.max_price != null ? `€${parseFloat(rule.max_price).toFixed(2)}` : '∞';
  return <Chip label={`${min} – ${max}`} size="small" variant="outlined" sx={{ fontWeight: 500 }} />;
};

const formatCoef = (coef) => {
  const c = parseFloat(coef);
  const pct = Math.round((c - 1) * 100);
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} sx={{ color: '#845adf' }}>
        × {c.toFixed(2)}
      </Typography>
      <Typography variant="caption" sx={{ color: '#6b7280' }}>
        {pct >= 0 ? `+${pct}%` : `${pct}%`} markup
      </Typography>
    </Box>
  );
};

const SortableRow = ({ rule, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rule.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    backgroundColor: isDragging ? '#f9fafb' : undefined
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ width: 50, p: 0.5 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: isDragging ? 'grabbing' : 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#9ca3af',
            '&:hover': { color: '#6b7280' }
          }}
        >
          <DragIndicatorIcon />
        </Box>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="medium">{rule.name}</Typography>
          <Typography variant="caption" color="text.secondary">ID: {rule.id}</Typography>
        </Box>
      </TableCell>
      <TableCell>{formatRange(rule)}</TableCell>
      <TableCell>{formatCoef(rule.coefficient)}</TableCell>
      <TableCell align="center">
        <Chip
          label={rule.active == 1 ? 'Active' : 'Inactive'}
          color={rule.active == 1 ? 'success' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell align="center">
        <IconButton size="small" onClick={() => onEdit(rule)} color="primary"><EditIcon /></IconButton>
        <IconButton size="small" onClick={() => onDelete(rule)} color="error"><DeleteIcon /></IconButton>
      </TableCell>
    </TableRow>
  );
};

const CoefficientRuleList = ({ rules, onEdit, onRefresh }) => {
  const [items, setItems] = useState(rules);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savedSnack, setSavedSnack] = useState(false);

  useEffect(() => { setItems(rules); }, [rules]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const persistOrder = async (orderedIds) => {
    setSavingOrder(true);
    try {
      const res = await fetch('https://api.elnaf.fi/api/coefficient_rules.php?action=reorder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: orderedIds })
      });
      const data = await res.json();
      if (!res.ok || data.result === 0) {
        alert(data.error || 'Failed to save order');
        await onRefresh();
        return;
      }
      setSavedSnack(true);
    } catch (e) {
      alert('Network error while saving order');
      await onRefresh();
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(r => r.id === active.id);
    const newIndex = items.findIndex(r => r.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    persistOrder(next.map(r => r.id));
  };

  const handleDeleteClick = (rule) => { setSelectedRule(rule); setDeleteDialogOpen(true); };

  const handleDeleteConfirm = async () => {
    if (!selectedRule) return;
    setDeleteLoading(true);
    try {
      const response = await fetch(`https://api.elnaf.fi/api/coefficient_rules.php?id=${selectedRule.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (response.ok && (data.result === 1 || data.success)) {
        setDeleteDialogOpen(false);
        setSelectedRule(null);
        await onRefresh();
      } else {
        alert(data.error || data.message || 'Failed to delete rule');
      }
    } catch (e) {
      alert('Failed to delete rule');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(r => r.id)} strategy={verticalListSortingStrategy}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 50 }}>
                    <Tooltip title="Drag to reorder — top of list has highest priority">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                        <DragIndicatorIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Base Price Range</TableCell>
                  <TableCell>Coefficient</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(rule => (
                  <SortableRow
                    key={rule.id}
                    rule={rule}
                    onEdit={onEdit}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SortableContext>
      </DndContext>

      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Coefficient Rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the rule "{selectedRule?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={20} color="inherit" />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={savedSnack}
        autoHideDuration={1800}
        onClose={() => setSavedSnack(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" variant="filled" sx={{ width: '100%' }}>Order saved</Alert>
      </Snackbar>

      {savingOrder && (
        <Box sx={{ position: 'fixed', bottom: 16, left: 16, display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(0,0,0,0.7)', color: 'white', px: 2, py: 1, borderRadius: 1, fontSize: 12 }}>
          <CircularProgress size={14} color="inherit" /> Saving order...
        </Box>
      )}
    </>
  );
};

export default CoefficientRuleList;
